import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Article } from "@/lib/types";
import { PageLoader, useMinLoading } from "@/components/Preloader";
import { TooltipProvider } from "@/components/ui/tooltip";
import { NewsletterCTA } from "@/components/NewsletterCTA";
import { FeaturedAnalysis, ResearchCard, ExplainersRail } from "@/components/home/ResearchSection";

const BUCKETS = [
  { key: "all", label: "All research" },
  { key: "markets", label: "Markets" },
  { key: "defi", label: "DeFi" },
  { key: "regulation", label: "Regulation" },
  { key: "onchain", label: "On-chain" },
  { key: "guides", label: "Guides" },
];
const INTER = { fontFamily: "Inter, 'Helvetica Neue', Helvetica, Arial, sans-serif" };

export default function ResearchPage() {
  const [bucket, setBucket] = useState("all");
  const [page, setPage] = useState(1);
  const [accumulated, setAccumulated] = useState<Article[]>([]);

  const { data, isLoading } = useQuery({
    queryKey: ["research-feed", bucket, page],
    queryFn: async () => (await api.get("/research/feed", { params: { bucket, page, limit: 12 } })).data,
  });
  const { data: base } = useQuery({
    queryKey: ["research-feed", "all", 1],
    queryFn: async () => (await api.get("/research/feed", { params: { bucket: "all", page: 1, limit: 12 } })).data,
  });

  const items: Article[] = page === 1 ? data?.items || [] : [...accumulated, ...(data?.items || [])];
  const showLoader = useMinLoading(isLoading, 600, `${bucket}-${page}`);
  const pick = (key: string) => { setBucket(key); setPage(1); setAccumulated([]); };

  return (
    <TooltipProvider delayDuration={150}>
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-10" data-testid="research-page">
      <header className="mb-6 sm:mb-8">
        <h1 style={INTER} className="text-[#f2f5fc] font-bold text-[32px] leading-[38px] sm:text-[44px] sm:leading-[52px] tracking-tight">
          Research &amp; Deep Dives
        </h1>
        <p style={INTER} className="text-[#969cab] text-[15px] sm:text-[16px] leading-[22px] mt-1 max-w-[762px]">
          Expert analysis, market intelligence, and deep research to navigate tomorrow.
        </p>
      </header>

      {base?.featured && (
        <div className="mb-6">
          <FeaturedAnalysis a={base.featured} />
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap" data-testid="research-filters">
        {BUCKETS.map((b) => (
          <button key={b.key} type="button" onClick={() => pick(b.key)} data-testid={`research-filter-${b.key}`}
            className={`h-8 px-3.5 rounded-full border nl-mono text-[11px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c24dff]/60 ${
              bucket === b.key
                ? "border-[#c24dff]/70 bg-[#1c0842] text-[#f5f7ff]"
                : "border-[#1c2436] bg-[#020306] text-[#a1abbd] hover:border-[#31405f] hover:text-white"}`}>
            {b.label}
          </button>
        ))}
        {data && !showLoader && (
          <span className="ml-auto nl-mono text-[11px] text-[#a1abbd]" data-testid="research-total">
            {data.total} {data.total === 1 ? "report" : "reports"}
          </span>
        )}
      </div>

      {showLoader && page === 1 ? (
        <div className="py-16"><PageLoader /></div>
      ) : items.length === 0 ? (
        <p className="nl-geist text-[13px] text-[#a1abbd] py-16 text-center" data-testid="research-empty">
          No qualifying research in this section yet — the desk publishes daily.
        </p>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 xl:gap-6" data-testid="research-grid">
            {items.map((a, i) => <ResearchCard key={a.slug} a={a} idx={i} />)}
          </div>
          {data && page < data.pages && (
            <div className="text-center mt-8">
              <button type="button" data-testid="research-load-more"
                onClick={() => { setAccumulated(items); setPage(page + 1); }}
                className="inline-flex items-center h-[38px] px-5 rounded-[7px] border border-[#1c2436] bg-[#020306] nl-geist text-[12px] font-medium text-[#c24dff] hover:border-[#872efa]/60 hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c24dff]/60">
                Load more research
              </button>
            </div>
          )}
        </>
      )}

      {(base?.explainers || []).length > 0 && (
        <div className="mt-6">
          <ExplainersRail items={base.explainers} />
        </div>
      )}

      <div className="mt-14">
        <NewsletterCTA sourceType="research_landing" interests={["research"]}
          heading="Never miss a research drop." />
      </div>
    </div>
    </TooltipProvider>
  );
}
